import { appVersionUpdated } from '@map/state/appVersion';
import { actionCreators as notificationActions } from '@map/state/notifications';
import { actionCreators as userActions } from '@map/state/user';
import { NotificationMessage } from '@map/types';
import { buildVersion } from '@shared/util/env';
import axios, { AxiosError, AxiosResponse, isAxiosError, isCancel } from 'axios';
import { DEV } from 'esm-env';
import { Store } from 'redux';

// this is still needed so axios' cancel exceptions don't show up during development
if (DEV) {
  window.addEventListener(
    'unhandledrejection',
    (event) => {
      const { reason } = event;
      if (reason && axios.isCancel(reason)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    { capture: true },
  );
}

export const parseAjaxError = (error: AxiosError): Array<Partial<NotificationMessage>> => {
  const { response, request, config } = error;
  if (response) {
    if (response.status === 403) {
      // i18n-tasks-use t('error_messages.missing_permission')
      return [{ textKey: 'error_messages.missing_permission' }];
    }
    if (response.status === 404) {
      // ignore 404 messages and don't show an error
      return [];
    }
    if (response.status === 429) {
      // i18n-tasks-use t('error_messages.too_many_requests')
      return [{ type: 'warning', textKey: 'error_messages.too_many_requests' }];
    }
    if (response.status === 503) {
      // i18n-tasks-use t('error_messages.service_unavailable')
      return [{ type: 'warning', textKey: 'error_messages.service_unavailable' }];
    }
    if (response.status >= 500) {
      // i18n-tasks-use t('error_messages.server_error')
      return [{ textKey: 'error_messages.server_error' }];
    }
    const { errors = [] } = response.data as { errors?: string[] };
    if (errors.length) {
      return errors.map((text) => ({ text }));
    }
  }
  if (request) {
    if (!navigator.onLine) {
      if (config?.method === 'get') {
        return [];
      }
      // i18n-tasks-use t('error_messages.offline_action')
      return [{ textKey: 'error_messages.offline_action' }];
    }
  }
  // i18n-tasks-use t('error_messages.unknown_network_error')
  return [{ textKey: 'error_messages.unknown_network_error' }];
};

const handlerEnabledSuccess = (config?: any) => {
  const handler = config?.handlerDisabled;

  if (handler && typeof handler === 'boolean') {
    return false;
  }

  if (handler?.silentSuccess) {
    return false;
  }

  return true;
};

const handlerEnabledError = (config?: any) => {
  const handler = config?.handlerDisabled;

  if (handler && typeof handler === 'boolean') {
    return false;
  }

  if (handler?.silentError) {
    return false;
  }

  return true;
};

type SetupParams = {
  timeout?: number;
  onMessage: (message: Pick<NotificationMessage, 'type' | 'text'>) => void;
  onAppUpdate: () => void;
  onLoggedOut: () => void;
};
const setupAxios = ({ onMessage, onAppUpdate, onLoggedOut, timeout = 30000 }: SetupParams) => {
  let appVersion = buildVersion;
  const checkVersion = (versionString?: string) => {
    if (!versionString) {
      return;
    }
    const version = parseFloat(versionString);
    if (isNaN(version) || version <= appVersion) {
      return;
    }
    appVersion = version;
    onAppUpdate();
  };
  axios.defaults.timeout = timeout;
  axios.defaults.headers.common['X-CSRF-PROTECTION'] = 1;

  // TODO: this is a legacy use of the /mobile prefix, find a better way to handle this
  const slugMatch = window.location.pathname.match(/^(\/mobile)?\/projects\/([^/]+)\//);
  if (slugMatch) {
    axios.defaults.params = { ...axios.defaults.params, slug: slugMatch[2]! };
  }

  axios.interceptors.response.use(
    (resp: AxiosResponse<{ messages?: NotificationMessage[] }>) => {
      checkVersion(resp.headers['x-app-version']);
      if (handlerEnabledSuccess(resp.config)) {
        const { messages = [] } = resp.data;
        messages.forEach((message) => onMessage(message));
      }
      return resp;
    },
    (error) => {
      if (isCancel(error)) {
        return Promise.reject(error);
      }

      if (isAxiosError(error)) {
        const { response, request } = error;
        if (response) {
          checkVersion(response.headers['x-app-version']);
        }
        const status = response?.status;

        // note, 401 isn't handled, the login form uses it

        if (status === 403) {
          // we've been logged out (403), unless the request was to the session scheduler
          if (!request?.responseURL || !request.responseURL.includes('/session_scheduler/')) {
            onLoggedOut();
          }
        } else if (handlerEnabledError(error.config)) {
          const responseMessages = parseAjaxError(error);
          responseMessages.forEach((message) => onMessage({ type: 'error', ...message }));
        }
      }
      return Promise.reject(error);
    },
  );
};

setupAxios.withDefaults = (store: Store) => {
  return setupAxios({
    onMessage: (msg) => store.dispatch(notificationActions.showMessage(msg)),
    onLoggedOut: () => store.dispatch(userActions.userLoggedOut()),
    onAppUpdate: () => store.dispatch(appVersionUpdated()),
    timeout: 30000,
  });
};

export default setupAxios;
